/**
 * Library (Knowledge) feature data layer + i18n namespace registration
 * (design revamp / handoff-2026-06).
 *
 * The Library screen has two views, each one server read via the typed
 * `@/lib/garden-api` fetchers (this file does NOT re-create the client):
 *
 *   1. `listArticles(lang)`     → the article index for `/library`
 *                                 (title + reading time per article).
 *   2. `getArticle(slug, lang)` → one article for `/library/:slug`
 *                                 (title + toc + body blocks). RU fallback
 *                                 happens server-side; a missing slug returns
 *                                 a 404 `ApiError` the reader renders as a
 *                                 friendly not-found state.
 *
 * Server state lives in TanStack Query; the screens read it through the hooks
 * below. The article body is a discriminated union of `KbHeadingBlock`
 * (`{h,p}`) and `KbCalloutBlock` (`{callout,text}`) — discriminate with
 * `isCalloutBlock` from `@/lib/garden-api`.
 *
 * i18n: the `library` namespace is registered here via
 * `i18n.addResourceBundle` (NOT in `@/lib/i18n`, which is outside this
 * feature's allow-list) — the exact pattern `features/dashboard/api.ts` and
 * `features/account/api.ts` use. All three locales (ru/ky/en) ship per
 * ADR-028; a missing KY/EN key falls back to RU (REQ-PRD-§13.2-A).
 *
 * No PII travels here — knowledge-base articles are public editorial content.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  getArticle,
  listArticles,
  type KbArticleDetail,
  type KbArticleListResponse,
} from "@/lib/garden-api";
import i18n, { initI18n, type SupportedLanguage } from "@/lib/i18n";

import libraryEn from "@/locales/en/library.json";
import libraryKy from "@/locales/ky/library.json";
import libraryRu from "@/locales/ru/library.json";

// ─────────────────────────── i18n registration ───────────────────────────

export const LIBRARY_NS = "library";

/**
 * Register the `library` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. `initI18n()` is idempotent and guarantees the instance is
 * initialised before `addResourceBundle` (the bundle methods only exist
 * post-init). The `hasResourceBundle` guard makes this safe to call from
 * every screen render and HMR-friendly. Called at module import for the
 * production path and at the top of each Library screen for the test path
 * (where init happens in `beforeEach`).
 */
export function registerLibraryI18n(): void {
  initI18n();
  if (typeof i18n.addResourceBundle !== "function") return;
  if (!i18n.hasResourceBundle("ru", LIBRARY_NS)) {
    i18n.addResourceBundle("ru", LIBRARY_NS, libraryRu, true, true);
  }
  if (!i18n.hasResourceBundle("ky", LIBRARY_NS)) {
    i18n.addResourceBundle("ky", LIBRARY_NS, libraryKy, true, true);
  }
  if (!i18n.hasResourceBundle("en", LIBRARY_NS)) {
    i18n.addResourceBundle("en", LIBRARY_NS, libraryEn, true, true);
  }
}

registerLibraryI18n();

// ─────────────────────────── language helper ───────────────────────────

/**
 * Narrow the live i18n language (which may carry a region tag like `en-US`)
 * to one of the supported KB languages. Mirrors `ChatRoute.resolveLanguage`.
 * The KB fetchers want a `SupportedLanguage`; an unknown base falls back to RU.
 */
export function resolveKbLanguage(raw: string): SupportedLanguage {
  const base = raw.split("-")[0] ?? "ru";
  return base === "ky" || base === "en" ? base : "ru";
}

// ─────────────────────────── query keys ───────────────────────────

/** Stable TanStack Query keys for the two Library reads. */
export const libraryQueryKeys = {
  /** Article index, per language. queryKey: ['kb','articles', lang]. */
  articles: (lang: SupportedLanguage): readonly unknown[] => [
    "kb",
    "articles",
    lang,
  ],
  /** One article, per slug + language. queryKey: ['kb','article', slug, lang]. */
  article: (slug: string, lang: SupportedLanguage): readonly unknown[] => [
    "kb",
    "article",
    slug,
    lang,
  ],
} as const;

// ─────────────────────────── hooks ───────────────────────────

/**
 * The article index for `/library`. Re-issued per language so a language
 * switch refetches the localized titles. Articles are public, slow-changing
 * editorial content — a generous `staleTime` avoids refetch churn within a
 * session without pinning a stale cache forever.
 */
export function useArticleList(
  lang: SupportedLanguage,
): UseQueryResult<KbArticleListResponse, Error> {
  return useQuery<KbArticleListResponse, Error>({
    queryKey: libraryQueryKeys.articles(lang),
    queryFn: () => listArticles(lang),
    staleTime: 5 * 60_000,
  });
}

/**
 * One article for `/library/:slug`. `retry: false` so a 404 (`ApiError`,
 * unknown slug) surfaces immediately as the not-found state instead of being
 * retried as a transient failure.
 */
export function useArticle(
  slug: string,
  lang: SupportedLanguage,
): UseQueryResult<KbArticleDetail, Error> {
  return useQuery<KbArticleDetail, Error>({
    queryKey: libraryQueryKeys.article(slug, lang),
    queryFn: () => getArticle(slug, lang),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
